<template>
    <div class="a-webinar-card" :class="{ ...className, ...basicClassName }">
        <div class="img-wrap">
            <a-picture
                v-if="!miniCard && image"
                class="img"
                :link="image"
                :alt="imageAlt || title"
                :position="picturePosition"
                fit="cover"
            />
        </div>
        <div class="info">
            <div class="date">
                <template v-if="label && !hasEnded">
                    <a-glyph class="a-accordion__icon" :name="label.glyph" />
                    <span>{{ label.text }}</span>
                </template>
                <template v-else-if="hasEnded">
                    <a-glyph class="a-accordion__icon" name="i-play-round--16" />
                    <span>{{ webinarOnDemandText }}</span>
                </template>
                <template v-else>
                    <a-glyph class="a-accordion__icon" name="i-clipboard--24" />
                    <span>{{ date }}</span>
                </template>
            </div>
            <div class="title">
                {{ title }}
            </div>
            <div class="desc">
                {{ desc }}
            </div>
            <div v-if="!detailsAfterSpeakers" class="details">
                <p v-if="detailsText" class="details-title">
                    {{ detailsText }}
                </p>
                <div class="details-items">
                    <div v-if="language" class="details-item">
                        <a-glyph name="i-chat--16" />
                        <span>{{ language }}</span>
                    </div>
                    <div v-if="duration" class="details-item">
                        <a-glyph name="i-clock--16" />
                        <span>{{ duration }}</span>
                    </div>
                </div>
            </div>
            <div class="bottom" :class="{'with-details': detailsAfterSpeakers }">
                <div v-if="!miniCard && speakers.length" class="speakers">
                    <p class="speakers-title">
                        {{ speakersText }}
                    </p>
                    <ul class="first-speakers">
                        <li
                            v-for="speaker in speakers"
                            :key="speaker.name"
                            class="first-speakers-item"
                        >
                            <span class="first-speakers-name">{{ speaker.name }},</span>
                            <span v-if="speaker.position" class="first-speakers-position">{{ speaker.position }}</span>
                        </li>
                    </ul>
                </div>
                <div v-if="detailsAfterSpeakers" class="details">
                    <p v-if="detailsText" class="details-title">
                        {{ detailsText }}
                    </p>
                    <div class="details-items">
                        <div v-if="language" class="details-item">
                            <a-glyph name="language-select2" />
                            <span>{{ language }}</span>
                        </div>
                        <div v-if="duration" class="details-item">
                            <a-glyph name="i-clock--16" />
                            <span>{{ duration }}</span>
                        </div>
                    </div>
                </div>
                <div class="link-wrap">
                    <a-link
                        v-if="miniCard"
                        v-bind="link"
                        :glyph="link.glyph || 'i-arrow-right--16'"
                        :type="theme === 'dark' ? 'light' : 'regular'"
                        :size="link.size || 'paragraph'"
                    />
                    <a-button
                        v-else
                        class="link"
                        v-bind="link"
                        :type="link.type || 'action'"
                        :size="link.size || 'small'"
                        :glyph="link.glyph || 'i-arrow-right--16'"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AButton from '@core/components/button/button.vue';
import AGlyph from '@core/components/glyph/glyph.vue';
import ALink from '@core/components/link/link.vue';
import APicture from '@core/components/picture/picture.vue';
import breakpoint from '@core/mixins/breakpoint.js';
import styleMixin from '@core/mixins/style.js';

export default {
    name: 'AWebinarCard',

    components: {
        AGlyph,
        AButton,
        ALink,
        APicture,
    },

    mixins: [breakpoint, styleMixin],

    props: {
        speakers: {
            type: Array,
            default: () => [],
        },

        webinarOnDemandText: {
            type: String,
            default: 'Webinar on demand',
        },

        detailsText: {
            type: String,
            default: undefined,
        },

        speakersText: {
            type: String,
            default: undefined,
        },

        hasEnded: {
            type: Boolean,
        },

        colorScheme: {
            type: String,
            validator: (prop) => ['green', 'blue'].includes(prop),
            default: 'green',
        },

        detailsAfterSpeakers: {
            type: Boolean,
            default: false,
        },

        date: {
            type: String,
            default: undefined,
        },

        label: {
            type: Object,
            default: undefined,
        },

        language: {
            type: String,
            default: undefined,
        },

        lang: {
            type: String,
            default: undefined,
        },

        duration: {
            type: String,
            default: undefined,
        },

        title: {
            type: String,
            required: true,
        },

        desc: {
            type: String,
            required: true,
        },

        link: {
            type: Object,
            default: () => ({}),
        },

        miniCard: {
            type: Boolean,
        },

        image: {
            type: String,
            default: undefined,
        },

        imageAlt: {
            type: String,
            default: undefined,
        },

        imagePosition: {
            type: String,
            // new cards are wide and focused off-center to the right
            default: '67% center',
        },

        imageMobilePosition: {
            type: String,
            default: '74% center',
        },

        cardShadow: {
            type: Boolean,
        },

        theme: {
            type: String,
            validator: (prop) => ['light', 'dark'].includes(prop),
            default: 'light',
        },
    },

    computed: {
        className() {
            return {
                'shadow': this.cardShadow,
                'mini': this.miniCard,
                'blue': this.colorScheme === 'blue',
                'dark': this.theme === 'dark',
            };
        },

        picturePosition() {
            return this.isMobile ? this.imageMobilePosition : this.imagePosition;
        },
    },
};
</script>

<style lang="pcss" scoped>
.a-webinar-card {
    color: var(--av-nav-primary);
    padding: 16px;
    display: flex;
    flex-direction: column;
    overflow: hidden;

    &.shadow {
        margin-top: 18px;
        box-shadow: var(--av-shadow-small);
        border-radius: 16px;
        border: 1px solid var(--av-brand-light);
        width: 100%;
    }

    &:not(.mini) {
        .desc {
            line-height: 24px;
        }
    }

    &.mini {
        padding: 24px 16px 8px 0;

        .date {
            margin-bottom: 8px;
        }
        .link-wrap {
            margin-top: 0;
        }
        .a-link {
            font-weight: 700;
        }
        .details-title {
            margin-bottom: 8px;
        }
    }

    &.mini:not(&.shadow) {
        .info {
            padding-inline-start: 0;
        }
    }

    &.blue {
        .date {
            color: var(--av-brand-primary);

            .a-glyph {
                fill: var(--av-brand-primary);
            }
        }

        .details {
            .details-item .a-glyph {
                fill: var(--av-brand-primary);
            }
        }
    }

    &.dark {
        color: var(--av-inversed-primary);

        .date .a-glyph,
        .details .details-item .a-glyph {
            fill: var(--av-inversed-primary);
        }

        .date,
        .title,
        .desc,
        .first-speakers-item {
            color: var(--av-inversed-primary);
        }
    }

    @media (--viewport-tablet) {
        padding: 24px;
        flex-direction: row;
        align-items: stretch;
    }

    :deep(.a-slider__nav) {
        margin-top: 24px;
        margin-bottom: 16px;
    }
}

.img-wrap {
    display: flex;
}

.img {
    border-radius: 8px;
    overflow: hidden;
    display: flex;
    min-height: 212px;
    max-height: 296px;
    width: 100%;
    height: auto;

    @media (--viewport-tablet) {
        display: block;
        min-height: 286px;
        max-height: none;
        min-width: 276px;
        max-width: 276px;
        width: auto;
    }

    @media (--viewport-desktop) {
        min-width: 286px;
        max-width: 286px;
    }

    :deep(.a-picture__img) {
        min-width: 100%;
        min-height: 100%;
    }
}

.info {
    padding-top: 16px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (--viewport-tablet) {
        padding-top: 0;
        padding-inline-start: 24px;
    }
}

.lang {
    @mixin body;

    margin-bottom: 8px;
    color: var(--av-brand-secondary);
}

.title {
    @mixin title-accent;

    color: var(--av-nav-primary);
    margin-bottom: 8px;
}

.desc {
    @mixin paragraph;

    margin-bottom: 8px;
}

.date {
    @mixin body-accent;
    color: var(--av-brand-primary);
    display: flex;
    align-items: center;

    .a-glyph {
        width: 16px;
        height: 16px;
        margin-top: 0;
        margin-inline-end: 6px;
        fill: var(--av-brand-primary);
    }
}

.details-items {
    @mixin descriptor;

    display: flex;
    gap: 16px;
    align-items: center;
}

.details-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .a-glyph {
        fill: var(--av-brand-primary);
    }
}

.bottom {
    margin-top: auto;
    padding-top: 24px;

    @media (--viewport-desktop) {
        display: flex;
        flex-direction: row;
        align-items: flex-end;
        justify-content: space-between;
    }

    .details {
        margin-top: 8px;
    }

    &.with-details {
        @media (--viewport-desktop) {
            align-items: flex-start;
            flex-wrap: wrap;

            .details {
                margin-top: 0;
                flex-basis: 256px;
            }

            .details-items {
                flex-direction: column;
                align-items: flex-start;
                gap: 0;
            }

            .link-wrap {
                width: 100%;
            }

            .link {
                margin-top: 24px;
            }
        }
    }
}

.speakers {
    @media (--viewport-desktop) {
        margin-bottom: 0;
        margin-inline-end: 24px;
    }
}

.speakers-title,
.details-title {
    @mixin paragraph-accent;

    color: var(--av-nav-primary);
}


.first-speakers-item {
    @mixin descriptor;
    color: var(--av-fixed-secondary);
}

.speakers-comma {
    display: none;
}

.link-wrap {
    margin-top: 24px;

    @media (--viewport-desktop) {
        margin-top: 0;
    }
}

.link {
    min-width: 240px;

    svg {
        display: none;
    }

    @media (--viewport-tablet) {
        justify-content: space-between;
        margin-inline-start: 0;

        svg {
            display: block;
        }
    }
}
</style>
